import { motion } from 'framer-motion';
import React, { useState } from 'react';

import { cn } from '@/lib/tw-merge/cn';

import { CONSULTATION_STEPS } from '../data/operations-data';

export default function ExperienceSection() {
  const [activeNumber, setActiveNumber] = useState('01');

  return (
    <section
      id="experience-section"
      className="w-full bg-[#99A0AE1A] py-12 sm:py-20"
    >
      <div className="app-container flex flex-col items-center gap-10">
        <div className="flex flex-col items-center gap-2">
          {/* Pill Badge */}
          <span className="mb-4 inline-flex h-9 items-center justify-center rounded-full border border-cloud-150 bg-surface-subtle/20 px-4 py-1 text-xs font-semibold uppercase tracking-[2.4px] text-primary shadow-sm">
            THE EXPERIENCE
          </span>

          {/* Section Heading */}
          <h2 className="text-center font-fraunces text-2xl font-normal leading-[28px] tracking-[-0.96px] text-foreground sm:text-4xl sm:leading-[48px] lg:text-[48px]">
            What Happens In A Live Consultation
          </h2>
        </div>
        {/* Main Dark Container with Dotted Dividers */}
        <div className="w-full rounded-[24px] border border-neutral-700/50 bg-[#262626] p-5 shadow-xl shadow-black/10 sm:rounded-[32px] lg:p-7">
          {/* Responsive Layout: 1 col on mobile (< md), 2 cols on tablet & desktop (>= md) */}
          <div className="relative grid grid-cols-1 md:grid-cols-2">
            {CONSULTATION_STEPS.map((step, index) => {
              const isActive = activeNumber === step.number;

              const mobileDividerClasses =
                index < CONSULTATION_STEPS.length - 1
                  ? 'border-b border-dashed border-[#7B7B7B] md:border-b-0'
                  : '';
              const desktopDividerClasses =
                index === 0
                  ? "md:before:absolute md:before:right-0 md:before:top-0 md:before:bottom-6 md:before:border-r md:before:border-dashed md:before:border-[#7B7B7B] md:before:content-[''] md:after:absolute md:after:bottom-0 md:after:left-0 md:after:block md:after:w-[200%] md:after:border-t md:after:border-dashed md:after:border-[#7B7B7B] md:after:content-['']"
                  : index === 2
                    ? "md:before:absolute md:before:right-0 md:before:top-6 md:before:bottom-0 md:before:border-r md:before:border-dashed md:before:border-[#7B7B7B] md:before:content-['']"
                    : '';
              const columnSpacingClass =
                index % 2 === 0 ? 'md:pr-12' : 'md:pl-6';
              const rowSpacingClass = index < 2 ? 'md:pb-6' : 'md:pt-6';

              return (
                <div
                  key={step.number}
                  id={`consultation-quadrant-${step.number}`}
                  onClick={() => setActiveNumber(step.number)}
                  onMouseEnter={() => setActiveNumber(step.number)}
                  className={cn(
                    'relative flex cursor-pointer items-stretch sm:px-3',
                    columnSpacingClass,
                    rowSpacingClass,
                    mobileDividerClasses,
                    desktopDividerClasses
                  )}
                >
                  <motion.div
                    layout
                    transition={{
                      type: 'spring',
                      stiffness: 350,
                      damping: 30,
                    }}
                    className={cn(
                      'flex min-h-[200px] w-full flex-col justify-start rounded-[16px] p-5 transition-all duration-300 sm:min-h-[220px] sm:rounded-[20px] sm:p-6 lg:p-7',
                      isActive
                        ? 'border border-[#0B4627] bg-foreground shadow-emerald-950/20'
                        : 'border border-transparent bg-transparent hover:bg-neutral-800/25'
                    )}
                  >
                    {/* Number and accent line header */}
                    <div className="mb-3.5 flex items-center gap-3.5">
                      <span className="rounded-2xl bg-[#333333] p-1.5 font-fraunces text-2xl font-bold leading-none tracking-tight text-white sm:text-[26px]">
                        {step.number}
                      </span>

                      {/* Orange accent line that progresses on hover */}
                      {isActive && (
                        <div className="h-[1px] flex-1 overflow-hidden rounded-full bg-[#FF5B1A10]">
                          <motion.div
                            initial={{ width: '0%' }}
                            animate={{ width: '100%' }}
                            transition={{
                              duration: 1,
                              ease: 'linear',
                            }}
                            className="h-full rounded-full bg-gradient-to-r from-[#FF5B1A80] via-[#FF5B1A60] to-transparent"
                          />
                        </div>
                      )}
                    </div>

                    {/* Step Title */}
                    <h3 className="mb-2.5 font-fraunces font-normal leading-snug tracking-tight text-white md:text-lg lg:text-2xl">
                      {step.title}
                    </h3>

                    {/* Step Description */}
                    <p className="text-[13px] leading-[26px] text-[#A3A3A3] sm:text-base">
                      {step.description}
                    </p>
                  </motion.div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
